import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  useDecideLoi,
  useExtendExclusivity,
  useLoiSellerVote,
  useRoomExclusivity,
  useRoomLoiOutcomes,
  useRoomLois,
  useSellerRoom,
  useSellerRoomAccess,
  useSession,
  useValidateExclusivity,
  useValidateExtension,
} from '@/api/hooks';
import { LoiManagementCard } from '@/components/LoiManagementCard';
import { loiAccess, sellerRooms } from '@/config';
import {
  ApiError,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ExclusivityPanel,
  HelpTip,
  LoiDocumentDialog,
  LoiOutcomeTable,
  errorMessage,
  loiDocumentContentPath,
  ownershipLowWarning,
  ownersMayStartLoiDecision,
} from '@dataroom/shared';
import type {
  LoiDecisionRequest,
  LoiOutcomeItem,
  LoiReviewItem,
  SellerDecision,
  SellerVoteTally,
} from '@dataroom/shared';

/**
 * Resolves the seller's room from the session identity, then renders the LOI
 * response surface for it — the owners' console for letters of intent.
 * Room-scoped hooks live in a child ({@link LoisPageInner}) so they mount only
 * once a concrete roomId exists (Rules of Hooks).
 */
export function LoisPage() {
  const access = useSellerRoomAccess();
  const session = useSession();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  // The sellers decide letters of intent. A team member with the Legal role may
  // read them and their outcome; any other team member (who has no LOIs tab, but
  // may follow a link here) gets a plain note and the page never asks the API.
  const room = sellerRooms(session.data).find((r) => r.roomId === access.roomId);
  const role = loiAccess(room);
  if (role === 'none') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Letters of intent are handled by the seller.
      </p>
    );
  }
  if (role === 'legal') return <LoisOutcomePage roomId={access.roomId} />;
  return <LoisPageInner roomId={access.roomId} />;
}

/**
 * What a seller's Legal team member sees: each LOI, its document (read-only) and
 * its final outcome. No responses, no exclusivity, nothing about the sellers'
 * votes: none of that is requested or rendered here.
 */
function LoisOutcomePage({ roomId }: { roomId: string }) {
  const lois = useRoomLoiOutcomes(roomId);
  const [viewing, setViewing] = useState<LoiOutcomeItem | null>(null);
  if (lois.error instanceof ApiError && lois.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Letters of intent</CardTitle>
        </CardHeader>
        <CardContent>
          {lois.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : lois.error ? (
            <p className="text-sm text-destructive">{errorMessage(lois.error)}</p>
          ) : lois.data.length > 0 ? (
            <LoiOutcomeTable lois={lois.data} onOpen={setViewing} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No letters of intent yet. They appear here once a buyer uploads one.
            </p>
          )}
        </CardContent>
      </Card>
      {viewing ? (
        <LoiDocumentDialog
          contentPath={loiDocumentContentPath(roomId, viewing.id)}
          filename={viewing.filename}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </div>
  );
}

function LoisPageInner({ roomId }: { roomId: string }) {
  const lois = useRoomLois(roomId);
  const room = useSellerRoom(roomId);
  const decide = useDecideLoi(roomId);
  // ADR-0046 enforced exclusivity: vote on the intermediary's proposed acceptance /
  // extension, extend (while the owners may respond to LOIs).
  const exclusivity = useRoomExclusivity(roomId);
  const loiVote = useLoiSellerVote(roomId);
  const validate = useValidateExclusivity(roomId);
  const validateExtension = useValidateExtension(roomId);
  const extend = useExtendExclusivity(roomId);
  const resetExclusivity = () => {
    validate.reset();
    validateExtension.reset();
    extend.reset();
  };
  // A seller's own counter / decline that did not complete is an open vote at
  // once: until the refreshed list arrives, show the tally the response carried.
  const loisView = withProvisionalVote(
    lois.data ?? [],
    decide.data?.sellerVote,
    decide.variables,
    decide.submittedAt,
    lois.dataUpdatedAt,
  );
  // The LOI whose signed document is open in the read-only viewer.
  const [viewing, setViewing] = useState<LoiReviewItem | null>(null);

  const unauthorized = [lois.error, room.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) return <Navigate to="/login" replace />;

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      {exclusivity.data ? (
        <ExclusivityPanel
          view={exclusivity.data}
          viewer="seller"
          countdownLabelAside={<HelpTip id="seller.lois.countdown" />}
          canExtend={!!room.data && ownersMayStartLoiDecision(room.data)}
          pending={validate.isPending || validateExtension.isPending || extend.isPending}
          error={
            errorMessage(validate.error) ??
            errorMessage(validateExtension.error) ??
            errorMessage(extend.error)
          }
          onValidate={(decision) => {
            resetExclusivity();
            validate.mutate({ decision });
          }}
          onValidateExtension={(decision) => {
            resetExclusivity();
            validateExtension.mutate({ decision });
          }}
          onExtend={(additionalDays) => {
            resetExclusivity();
            extend.mutate({ additionalDays });
          }}
        />
      ) : null}
      <LoiManagementCard
        lois={loisView}
        loading={lois.isPending}
        error={errorMessage(lois.error)}
        actionError={errorMessage(decide.error)}
        canRespond={!!room.data && ownersMayStartLoiDecision(room.data)}
        toggleReady={room.data != null}
        decidingId={decide.isPending ? (decide.variables?.loiId ?? null) : null}
        onDecide={(loiId, decision, terms) =>
          decide.mutate({ loiId, body: terms ? { decision, exclusivity: terms } : { decision } })
        }
        onOpen={setViewing}
        onDismissError={() => decide.reset()}
        onVote={(loiId, vote) => {
          loiVote.reset();
          loiVote.mutate({ loiId, vote });
        }}
        votingId={loiVote.isPending ? (loiVote.variables?.loiId ?? null) : null}
        voteError={
          loiVote.error && loiVote.variables
            ? { loiId: loiVote.variables.loiId, message: errorMessage(loiVote.error) ?? '' }
            : null
        }
        timezone={room.data?.timezone ?? exclusivity.data?.timezone ?? 'UTC'}
      />
      {viewing ? (
        <LoiDocumentDialog
          contentPath={loiDocumentContentPath(roomId, viewing.id)}
          filename={viewing.filename}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The page's LOI list with the vote a seller's own counter / decline just opened
 * (the decision response's tally), shown at once rather than after the next
 * refresh. Only until the list has been refetched since that response, and only
 * for an LOI that still has no vote of its own. An accept's vote is the
 * exclusivity panel's, never here.
 */
function withProvisionalVote(
  lois: LoiReviewItem[],
  tally: SellerVoteTally | null | undefined,
  sent: { loiId: string; body: LoiDecisionRequest } | undefined,
  submittedAt: number,
  listUpdatedAt: number,
): LoiReviewItem[] {
  if (!tally || !sent || sent.body.decision === 'accept' || listUpdatedAt > submittedAt) {
    return lois;
  }
  return lois.map((l) => {
    if (l.id !== sent.loiId || l.sellerVote || l.status !== 'submitted') return l;
    const vote: SellerDecision = {
      kind: 'loi_response',
      subjectId: l.id,
      loiId: l.id,
      action: sent.body.decision === 'counter' ? 'counter' : 'reject',
      proposedBy: null,
      proposedByRole: 'seller',
      buyerEmail: l.actorEmail,
      startDate: null,
      lengthDays: null,
      additionalDays: null,
      sellers: tally.sellers,
      approvedPct: tally.approvedPct,
      declinedPct: tally.declinedPct,
      totalPct: tally.totalPct,
      canApprove: tally.canApprove,
      warning: ownershipLowWarning(tally.totalPct),
      viewerVote: 'approve',
      viewerCanVote: true,
    };
    return { ...l, sellerVote: vote };
  });
}
